import React, { useState } from 'react';
import { X, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const DecisionDialog = ({ decisionType, decisionId, onClose, onSuccess }) => {
  const [outcomeDate, setOutcomeDate] = useState('');
  const [revisitDate, setRevisitDate] = useState('');
  const [loading, setLoading] = useState(false);

  const getMessage = () => {
    if (decisionType === 'YES' || decisionType === 'NO') {
      return "Trust your knowing. Let's track how this unfolds.";
    }
    return "Sometimes waiting IS the decision. Trust your timing.";
  };

  const handleSubmit = async () => {
    if (decisionType === 'WAIT' && !revisitDate) {
      toast.error('Please select when to revisit this decision');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/decisions/${decisionId}/make-decision`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            decision: decisionType,
            outcome_date: outcomeDate || null,
            revisit_date: revisitDate || null
          })
        }
      );

      if (response.ok) {
        onSuccess();
      } else {
        toast.error('Failed to save decision');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="decision-dialog">
      <div className="glass-card p-8 max-w-md w-full relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          data-testid="close-decision-dialog"
        >
          <X className="w-6 h-6" />
        </button>

        <div>
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Decision: {decisionType}
          </h2>
          <p className="text-lg text-gray-700 mb-6" data-testid="decision-message">
            {getMessage()}
          </p>

          {(decisionType === 'YES' || decisionType === 'NO') && (
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">
                When will you know the outcome? (Optional)
              </label>
              <input
                data-testid="outcome-date-input"
                type="date"
                value={outcomeDate}
                onChange={(e) => setOutcomeDate(e.target.value)}
                className="input-field"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          )}

          {decisionType === 'WAIT' && (
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">
                When would you like to revisit this? *
              </label>
              <input
                data-testid="revisit-date-input"
                type="date"
                value={revisitDate}
                onChange={(e) => setRevisitDate(e.target.value)}
                className="input-field"
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="btn-primary flex-1"
              data-testid="save-decision-btn"
            >
              {loading ? 'Saving...' : 'Save Decision'}
            </button>
            <button
              onClick={onClose}
              className="btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DecisionDialog;
